import React, { useState, useEffect, useLayoutEffect, useContext, useRef, useCallback } from 'react';
import cn from 'classnames';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { AppContext } from '../../AppContext';
import { TabBar } from '../TabBar';
import { ImageCountConfig } from './ImageCountConfig';
import { FormHeaderConfig } from './FormHeaderConfig';
import ServiceTypeConfiguration from '../ServiceTypeConfiguration';
import {
  getSelfInspectImageConfigs,
  getSelfInspectImageConfigPropertyTypes,
  getSelfInspectImageConfigProducts,
} from '../../services/systemConfiguration.service';
import { getFormHeaderConfig } from '../../services/formheader.service';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    height: '100%',
    boxSizing: 'border-box',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridAutoRows: 'min-content',
    alignContent: 'start',
    gap: theme.spacing(4),
  },
  spanThree: {
    gridColumn: 'span 3',
    marginBottom: theme.spacing(4),
  },
  spanFull: {
    gridColumn: '1 / -1',
    marginBottom: theme.spacing(4),
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
  tabContent: {
    padding: theme.spacing(3),
    boxSizing: 'border-box',
  },
  formHeaderTabContent: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
}));

export const SystemConfigurations = () => {
  const classes = useStyles();
  const { dispatch } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [imageConfigs, setImageConfigs] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [formHeaderConfigs, setFormHeaderConfigs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const formHeaderTabRef = useRef(null);
  const [formHeaderTabHeight, setFormHeaderTabHeight] = useState(null);

  const updateFormHeaderTabHeight = useCallback(() => {
    if (formHeaderTabRef.current) {
      const top = formHeaderTabRef.current.getBoundingClientRect().top;
      setFormHeaderTabHeight(Math.max(window.innerHeight - top - 24, 200));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useLayoutEffect(() => {
    updateFormHeaderTabHeight();
    window.addEventListener('resize', updateFormHeaderTabHeight);
    return () => window.removeEventListener('resize', updateFormHeaderTabHeight);
  }, [activeTab, loading, updateFormHeaderTabHeight]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configData, propertyTypeData, productData, formHeaderConfigData] = await Promise.all([
        getSelfInspectImageConfigs(),
        getSelfInspectImageConfigPropertyTypes(),
        getSelfInspectImageConfigProducts(),
        getFormHeaderConfig(),
      ]);
      setImageConfigs(configData);
      setPropertyTypes(propertyTypeData);
      setProducts(productData);
      setFormHeaderConfigs(formHeaderConfigData);
    } catch (err) {
      console.error('Failed to load system configuration data:', err);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: { isOpen: true, message: 'Failed to load configurations', variant: 'error' },
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      label: 'DIY Configuration',
      key: 'diy',
      component: (
        <div className={classes.grid}>
          <div className={classes.spanFull}>
            <ImageCountConfig
              configs={imageConfigs}
              propertyTypes={propertyTypes}
              products={products}
              onRefresh={loadData}
            />
          </div>

          {/* Additional DIY/self-inspect configuration cards go here — single column: <div>, three-quarter width: <div className={classes.spanThree}>, full width: <div className={classes.spanFull}> */}
        </div>
      ),
    },
    {
      label: 'FormHeader Configuration',
      key: 'formHeader',
      component: (
        <div
          ref={formHeaderTabRef}
          className={classes.formHeaderTabContent}
          style={{ height: formHeaderTabHeight ? `${formHeaderTabHeight}px` : undefined }}
        >
          <FormHeaderConfig formHeaders={formHeaderConfigs} />
        </div>
      ),
    },
    {
      label: 'Service Type Configuration',
      key: 'serviceType',
      component: <ServiceTypeConfiguration />,
    },
  ];

  const changeTab = (key) => {
    setActiveTab(tabs.findIndex((tab) => tab.key === key));
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingWrapper}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex-1', 'col-center-start')}>
      <TabBar tabs={tabs} changeTab={changeTab} activeTab={activeTab} />
      <div className={cn('grid-container', classes.tabContent)} style={{ marginLeft: 0, marginRight: 0 }}>
        {tabs[activeTab].component}
      </div>
    </div>
  );
};
